(() => {
  if (customElements.get('td-editorial-header')) return;

  class TDEditorialHeader extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.navigation = this.querySelector('[data-td-navigation]');
      this.fallback = this.querySelector('[data-td-menu-fallback]');
      this.menuButton = this.querySelector('[data-td-menu-open]');
      this.menuDialog = this.querySelector('#td-header-menu-dialog');
      this.mobileSlot = this.querySelector('[data-td-mobile-slot]');
      this.desktopSlot = this.querySelector('[data-td-desktop-slot]');
      if (!this.navigation || !this.menuDialog || typeof this.menuDialog.showModal !== 'function') return;
      this.desktopQuery = window.matchMedia('(min-width: 1200px)');
      this.menuOpen = false;
      this.overlayOpen = false;
      this.lastY = Math.max(window.scrollY, 0);
      this.direction = 0;
      this.distance = 0;
      this.frame = 0;
      this.setAttribute('data-enhanced', '');
      this.fallback.hidden = true;
      this.menuButton.hidden = false;
      this.syncNavigation();

      this.desktopQuery.addEventListener('change', () => this.syncNavigation(), options);
      this.menuButton.addEventListener('click', () => this.openMenu(), options);
      this.querySelector('[data-td-menu-close]').addEventListener('click', () => this.closeMenu(), options);
      this.menuDialog.addEventListener('close', () => this.finishMenuClose(), options);
      this.menuDialog.addEventListener('click', event => {
        if (event.target === this.menuDialog) {
          const rect = this.menuDialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeMenu();
        }
      }, options);

      this.navigation.addEventListener('toggle', event => {
        const opened = event.target;
        if (!opened.matches('[data-td-category]') || !opened.open) return;
        this.navigation.querySelectorAll('[data-td-category][open]').forEach(item => {
          if (item !== opened) item.open = false;
        });
        this.reveal();
        this.track({ event: 'td_header_category_open', location: this.menuOpen ? 'mobile_menu' : 'desktop_navigation', category: opened.querySelector('summary span')?.textContent.trim() || '' });
      }, { ...options, capture: true });

      this.addEventListener('click', event => {
        const link = event.target.closest('[data-td-header-nav]');
        if (link) {
          let destination = '';
          try { destination = new URL(link.href, window.location.origin).pathname; } catch (_) {}
          this.track({ event: 'td_header_navigation_click', location: this.menuOpen ? 'mobile_menu' : 'desktop_navigation', destination, category: link.dataset.navLabel || '' });
        }
        if (event.target.closest('[data-td-header-cart]')) this.track({ event: 'td_header_cart_open', location: 'header' });
      }, options);

      document.addEventListener('click', event => {
        if (!this.contains(event.target)) this.closeCategories();
      }, options);
      document.addEventListener('focusin', event => {
        if (!this.contains(event.target)) this.closeCategories();
      }, options);
      this.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        const expanded = event.target.closest('[data-td-category][open]');
        if (expanded) {
          event.preventDefault();
          expanded.open = false;
          expanded.querySelector('summary').focus();
        }
      }, options);
      this.addEventListener('focusin', () => this.reveal(), options);
      document.addEventListener('td:header:search-opening', () => { this.closeMenu(false); this.closeCategories(); this.reveal(); }, options);
      document.addEventListener('td:header:overlay-change', event => {
        this.overlayOpen = Boolean(event.detail?.open);
        this.reveal();
        this.lastY = Math.max(window.scrollY, 0);
      }, options);
      document.addEventListener('td:header:analytics', event => this.track(event.detail || {}), options);
      window.addEventListener('scroll', () => {
        if (this.frame) return;
        this.frame = window.requestAnimationFrame(() => { this.frame = 0; this.onScroll(); });
      }, { ...options, passive: true });
      window.addEventListener('pageshow', () => { this.reveal(); this.lastY = Math.max(window.scrollY, 0); }, options);
      this.resizeObserver = new ResizeObserver(() => {
        document.documentElement.style.setProperty('--td-header-height', `${Math.round(this.offsetHeight)}px`);
      });
      this.resizeObserver.observe(this);
    }

    disconnectedCallback() {
      this.closeMenu(false);
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      if (this.frame) window.cancelAnimationFrame(this.frame);
    }

    syncNavigation() {
      this.closeMenu(false);
      this.closeCategories();
      (this.desktopQuery.matches ? this.desktopSlot : this.mobileSlot).appendChild(this.navigation);
      this.reveal();
    }

    openMenu() {
      if (this.desktopQuery.matches || this.menuDialog.open) return;
      this.reveal();
      this.hadOverflowLock = document.body.classList.contains('overflow-hidden');
      document.body.classList.add('td-header-menu-open', 'overflow-hidden');
      this.restoreMenuFocus = true;
      this.menuOpen = true;
      this.menuDialog.showModal();
      this.menuButton.setAttribute('aria-expanded', 'true');
      this.track({ event: 'td_header_menu_open', location: 'header' });
    }

    closeMenu(restoreFocus = true) {
      if (!this.menuDialog || !this.menuOpen) return;
      this.restoreMenuFocus = restoreFocus;
      if (this.menuDialog.open) this.menuDialog.close();
      this.finishMenuClose();
    }

    finishMenuClose() {
      if (!this.menuOpen) return;
      this.menuOpen = false;
      document.body.classList.remove('td-header-menu-open');
      if (!this.hadOverflowLock && !document.querySelector('dialog[open], cart-drawer.active')) document.body.classList.remove('overflow-hidden');
      this.menuButton.setAttribute('aria-expanded', 'false');
      this.closeCategories();
      this.reveal();
      if (this.restoreMenuFocus && !this.desktopQuery.matches) this.menuButton.focus({ preventScroll: true });
    }

    closeCategories() {
      this.navigation?.querySelectorAll('[data-td-category][open]').forEach(item => { item.open = false; });
    }

    reveal() {
      this.dataset.hidden = 'false';
      this.distance = 0;
    }

    onScroll() {
      const y = Math.max(window.scrollY, 0);
      const delta = y - this.lastY;
      this.lastY = y;
      if (this.dataset.sticky === 'false' || Math.abs(delta) < 2) return;
      if (y < 120 || this.menuOpen || this.overlayOpen || this.contains(document.activeElement) || this.querySelector('[data-td-category][open]') || document.body.classList.contains('overflow-hidden')) {
        this.reveal();
        return;
      }
      const direction = delta > 0 ? 1 : -1;
      if (direction !== this.direction) this.distance = 0;
      this.direction = direction;
      this.distance += Math.abs(delta);
      if (direction === 1 && this.distance > 40) this.dataset.hidden = 'true';
      if (direction === -1 && this.distance > 24) this.reveal();
    }

    track(detail) {
      if (!detail || typeof detail.event !== 'string' || !/^td_header_[a-z_]+$/.test(detail.event)) return;
      const privacy = window.Shopify?.customerPrivacy;
      if (typeof privacy?.analyticsProcessingAllowed !== 'function' || !privacy.analyticsProcessingAllowed()) return;
      if (!Array.isArray(window.dataLayer)) return;
      const payload = { event: detail.event, page_type: this.dataset.pageType || '', header_version: 'editorial_v1' };
      ['location', 'category', 'destination', 'query_length', 'product_id', 'result_count', 'result_type'].forEach(key => {
        const value = detail[key];
        if (typeof value === 'string') payload[key] = value.slice(0, 200);
        else if (typeof value === 'number' && Number.isFinite(value)) payload[key] = value;
      });
      window.dataLayer.push(payload);
    }
  }
  customElements.define('td-editorial-header', TDEditorialHeader);
})();
